
import { Component, Vue, Watch } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import WebsiteRepositoryInterface, {
  Website,
  WebsiteCreation,
  WebsiteDeletion,
  WebsiteOrchestratorResourceType,
} from '@/services/Website/interfaces';
import { WebsiteMigration } from '@/services/WebsiteMigrations/interfaces';
import PageHeader from '@/components/layout/PageHeader.vue';
import StatusChip from '@/components/layout/StatusChip.vue';
import DeletionOrchestratorStatus from '@/components/website/DeletionOrchestratorStatus.vue';
import { latestFromLoadedResources } from '@/utils/websiteOrchestrator';

@Component({
  inject: [
    'websiteRepository',
  ],
  components: {
    PageHeader,
    StatusChip,
    DeletionOrchestratorStatus,
  },
})
export default class SiteOrchestrator extends Vue {
  public loading = false;

  public orchLoading = false;

  public websiteRepository!: WebsiteRepositoryInterface;

  public websiteId = 0;

  public website: Website | null = null;

  public creations: WebsiteCreation[] = [];

  public deletions: WebsiteDeletion[] = [];

  public migrations: WebsiteMigration[] = [];

  public latestCreation: WebsiteCreation | null = null;

  public latestDeletion: WebsiteDeletion | null = null;

  public resourceTypes = {
    CREATION: WebsiteOrchestratorResourceType.CREATION,
    DELETION: WebsiteOrchestratorResourceType.DELETION,
    MIGRATION: WebsiteOrchestratorResourceType.MIGRATION,
  };

  public creationHeaders: DataTableHeader[] = [
    { text: 'ID', value: 'id', width: '80px' },
    { text: 'Status', value: 'status' },
    { text: 'Created', value: 'createdDate' },
    { text: 'Updated', value: 'updatedDate' },
  ];

  public deletionHeaders: DataTableHeader[] = [
    { text: 'ID', value: 'id', width: '80px' },
    { text: 'Type', value: 'type' },
    { text: 'Status', value: 'status' },
    { text: 'Created', value: 'createdDate' },
    { text: 'Updated', value: 'updatedDate' },
  ];

  public migrationHeaders: DataTableHeader[] = [
    { text: 'ID', value: 'id', width: '80px' },
    { text: 'Source', value: 'sourceWebsiteId' },
    { text: 'Target', value: 'targetWebsiteId' },
    { text: 'Type', value: 'type' },
    { text: 'Status', value: 'status' },
    { text: 'Created', value: 'createdDate' },
  ];

  public async created(): Promise<void> {
    this.websiteId = Number(this.$route.params.websiteId);
    await this.load();
  }

  @Watch('$route.params.websiteId')
  public async onWebsiteIdChange(): Promise<void> {
    this.websiteId = Number(this.$route.params.websiteId);
    await this.load();
  }

  public async load(): Promise<void> {
    if (!this.websiteId || this.websiteId < 1) {
      this.$errorDialog.show('Invalid website id.');
      return;
    }

    this.loading = true;
    this.orchLoading = true;
    this.creations = [];
    this.deletions = [];
    this.migrations = [];
    this.latestCreation = null;
    this.latestDeletion = null;

    try {
      const [composite, resources] = await Promise.all([
        this.websiteRepository.getWebsiteById(this.websiteId),
        this.websiteRepository.getWebsiteOrchestratorResources(this.websiteId),
      ]);

      this.website = composite.website;
      this.creations = resources.creations || [];
      this.deletions = resources.deletions || [];
      this.migrations = resources.migrations || [];
      this.loading = false;

      try {
        const latest = await latestFromLoadedResources(
          this.websiteRepository,
          this.websiteId,
          this.creations,
          this.deletions,
        );
        this.latestCreation = latest.creation;
        this.latestDeletion = latest.deletion;
      } catch (err) {
        this.latestCreation = null;
        this.latestDeletion = null;
      } finally {
        this.orchLoading = false;
      }
    } catch (err) {
      this.loading = false;
      this.orchLoading = false;
      this.$errorDialog.show('Failed to load website orchestrator resources.');
    }
  }

  public async viewResource(resourceId: number, resourceType: number): Promise<void> {
    await this.$router.push({
      name: 'view_resource',
      params: {
        websiteId: String(this.websiteId),
        resourceType: String(resourceType),
        resourceId: String(resourceId),
      },
    });
  }

  public transformKebabCase(str: string): string {
    return (str || '')
      .replaceAll('-', ' ')
      .toUpperCase();
  }
}
